import React, { useState, useEffect, useCallback } from 'react';
import PageHeader from '../components/common/PageHeader';
import { CarbonCareDatePicker } from '../components/shared/CarbonCareDatePicker';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
} from '@mui/material';
import {
  SortableHeaderCell,
  makeSortPaginationHandlers,
  AdminTablePagination,
} from './_shared/adminListShared_tableUtils';
import {
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { auditApi } from '../services/api/auditApi';
import { companyApi } from '../services/api/companyApi';
import { AuditLog } from '../types/audit';
import { Company } from '../types/company';
import { useAuthStore } from '../store/authStore';
import { AccountType } from '../types/auth';
import { format } from 'date-fns';

const AuditLogsPage: React.FC = () => {
  const { user: currentUser } = useAuthStore();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [availableActions, setAvailableActions] = useState<string[]>([]);

  // Filters
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [selectedAction, setSelectedAction] = useState<string>('');
  const [performedByEmailFilter, setPerformedByEmailFilter] = useState<string>('');


  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  // Sorting
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Detail Modal
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const loadCompanies = useCallback(async () => {
    try {
      const result = await companyApi.getAll({ limit: 1000 });
      setCompanies(result.data);
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  }, []);

  const loadActions = useCallback(async () => {
    try {
      const actions = await auditApi.getActions();
      setAvailableActions(actions);
    } catch (error) {
      console.error('Failed to load actions:', error);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
        sortBy,
        sortOrder: sortDir,
      };
      if (selectedCompanyId) params.companyId = selectedCompanyId;
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (selectedAction) params.action = selectedAction;
      if (performedByEmailFilter) params.performedByEmail = performedByEmailFilter;

      const result = await auditApi.getAll(params);
      setLogs(result.data);
      setTotalRows(result.total);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId, fromDate, toDate, selectedAction, performedByEmailFilter, page, rowsPerPage, sortBy, sortDir]);

  useEffect(() => {
    if (currentUser && (currentUser.accountType === AccountType.CarbonCareAdmin || currentUser.accountType === AccountType.MasterAdmin)) {
      loadCompanies();
      loadActions();
    }
  }, [currentUser, loadCompanies, loadActions]);

  // Fetch logs whenever pagination or filters change
  useEffect(() => {
    if (currentUser && currentUser.accountType >= AccountType.CarbonCareAdmin) {
      loadLogs();
    }
  }, [loadLogs]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [selectedCompanyId, fromDate, toDate, selectedAction, performedByEmailFilter]);

  const handleRefresh = () => {
    loadLogs();
  };

  const handleOpenDetail = (log: AuditLog) => {
    setSelectedLog(log);
    setDetailOpen(true);
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'CREATE': return 'success';
      case 'UPDATE': return 'info';
      case 'DELETE': return 'error';
      default: return 'default';
    }
  };
  const getActionLabel = (action: string) => {
    switch (action) {
      case 'CREATE':
      case 'UPDATE':
      case 'DELETE': 
      case 'LOGIN':
        return action;
      case 'LOGIN_SSO':
        return 'LOGIN SSO';
      case 'ACCOUNT_LOCKED':
        return 'ACCOUNT LOCKED';
      case 'LOGIN_FAILED':
        return 'LOGIN FAILED';
      default: return action;
    }
  };

  const { handleChangePage, handleChangeRowsPerPage, handleSort } = makeSortPaginationHandlers(
    { page, rowsPerPage, sortBy, sortDir },
    setPage,
    setRowsPerPage,
    setSortBy,
    setSortDir,
  );
  const sortableHeader = (field: string, label: string) => (
    <SortableHeaderCell field={field} label={label} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
  );

  const getLicenseeName = (companyId: number | null | undefined) => {
    if (!companyId) return 'N/A';
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : `ID: ${companyId}`;
  };

  if (!currentUser || (currentUser.accountType < AccountType.CarbonCareAdmin)) {
    return (
      <Box>
        <Typography color="error">Access denied. Only admins can view audit logs.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        description="Review system activity and user actions across the platform."
        actions={
          <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Refresh
            </Button>
        }
      />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Licensee</InputLabel>
                <Select
                  value={selectedCompanyId ?? ''}
                  label="Licensee"
                  onChange={(e) => setSelectedCompanyId(e.target.value === '' ? null : Number(e.target.value))}
                >
                  <MenuItem value=""><em>All Licensees</em></MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Action</InputLabel>
                <Select
                  value={selectedAction}
                  label="Action"
                  onChange={(e) => setSelectedAction(e.target.value)}
                >
                  <MenuItem value=""><em>All Actions</em></MenuItem>
                  {availableActions.map((action) => (
                    <MenuItem key={action} value={action}>{action}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Performed By Email"
                size="small"
                placeholder="Filter by email..."
                value={performedByEmailFilter}
                onChange={(e) => setPerformedByEmailFilter(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') loadLogs(); }}
              />
            </Grid>
            <Grid item xs={12} md={3} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <CarbonCareDatePicker
                label="From"
                placeholder="From Date"
                value={fromDate || null}
                onChange={(newFrom) => {
                  setFromDate(newFrom);
                  if (toDate && toDate < newFrom) setToDate('');
                }}
                maxDate={toDate ? new Date(toDate + 'T00:00:00') : new Date()}
                portalId="root"
                sx={{ width: 150 }}
              />
              <CarbonCareDatePicker
                label="To"
                placeholder="To Date"
                value={toDate || null}
                onChange={setToDate}
                minDate={fromDate ? new Date(fromDate + 'T00:00:00') : undefined}
                maxDate={new Date()}
                portalId="root"
                sx={{ width: 150 }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={sortBy === 'id' ? sortDir : false}>
                {sortableHeader('id', 'ID')}
              </TableCell>
              <TableCell sortDirection={sortBy === 'created_at' ? sortDir : false}>
                {sortableHeader('created_at', 'Time Stamp')}
              </TableCell>
              <TableCell sortDirection={sortBy === 'company_id' ? sortDir : false}>
                {sortableHeader('company_id', 'Licensee')}
              </TableCell>
              <TableCell sortDirection={sortBy === 'performed_by_email' ? sortDir : false}>
                {sortableHeader('performed_by_email', 'Performed By')}
              </TableCell>
              <TableCell sortDirection={sortBy === 'resource_type' ? sortDir : false}>
                {sortableHeader('resource_type', 'Resource Type')}
              </TableCell>
              <TableCell sortDirection={sortBy === 'resource_id' ? sortDir : false}>
                {sortableHeader('resource_id', 'Resource ID')}
              </TableCell>
              <TableCell sortDirection={sortBy === 'action' ? sortDir : false}>
                {sortableHeader('action', 'Action')}
              </TableCell>
              <TableCell align="center">Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(() => {
              if (loading) {
                return (
              Array.from({ length: 10 }, (_, i) => `audit-skeleton-${i}`).map((rowKey, idx) => (
                <TableRow key={rowKey}>
                  <TableCell><Skeleton variant="text" width={40} /></TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <Skeleton variant="text" width={80} />
                      <Skeleton variant="text" width={70} />
                    </Box>
                  </TableCell>
                  <TableCell><Skeleton variant="text" width={140} /></TableCell>
                  <TableCell><Skeleton variant="text" width={`${50 + ((idx * 9) % 30)}%`} /></TableCell>
                  <TableCell><Skeleton variant="text" width={100} /></TableCell>
                  <TableCell><Skeleton variant="text" width={50} /></TableCell>
                  <TableCell><Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: '12px' }} /></TableCell>
                  <TableCell align="center"><Skeleton variant="circular" width={28} height={28} sx={{ mx: 'auto' }} /></TableCell>
                </TableRow>
              ))
                );
              }
              if (logs.length === 0) {
                return (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No Data Found</Typography>
                    </TableCell>
                  </TableRow>
                );
              }
              return (
                  logs.map((log) => (
                    <TableRow key={log.id} hover>
                      <TableCell>{log.id}</TableCell>
                      <TableCell>
                        {log.created_at ? (
                          <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <span>{format(new Date(log.created_at), 'yyyy-MM-dd')}</span>
                            <span>{format(new Date(log.created_at), 'HH:mm:ss')}</span>
                          </Box>
                        ) : '-'}
                      </TableCell>
                      <TableCell>{getLicenseeName(log.company_id)}</TableCell>
                      <TableCell>{log.performed_by_email || 'N/A'}</TableCell>
                      <TableCell>{log.resource_type}</TableCell>
                      <TableCell>{log.resource_id}</TableCell>
                      <TableCell>
                        <Chip
                          label={getActionLabel(log.action)}
                          color={getActionColor(log.action) as any}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton onClick={() => handleOpenDetail(log)}>
                          <VisibilityIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                );
            })()}
          </TableBody>
        </Table>
        {!loading && (
          <AdminTablePagination
            count={totalRows}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </TableContainer>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: 'f5f5f5', borderBottom: '1px solid #ddd' }}>
          Audit Log Details - ID #{selectedLog?.id}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedLog && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Resource</Typography>
                <Typography variant="body1">{selectedLog.resource_type} ({selectedLog.resource_id})</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Action</Typography>
                <Chip label={selectedLog.action} color={getActionColor(selectedLog.action) as any} size="small" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Performed By</Typography>
                <Typography variant="body1">{selectedLog.performed_by_email || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">User Agent</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{selectedLog.user_agent || 'N/A'}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Value Changes</Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#fffbfb' }}>
                  <Typography variant="subtitle2" color="error" gutterBottom>Previous Values</Typography>
                  <pre style={{ fontSize: '0.8rem', overflow: 'auto', maxHeight: '300px' }}>
                    {selectedLog.previous_values ? JSON.stringify(selectedLog.previous_values, null, 2) : 'None'}
                  </pre>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#fbfffb' }}>
                  <Typography variant="subtitle2" color="success.main" gutterBottom>New Values</Typography>
                  <pre style={{ fontSize: '0.8rem', overflow: 'auto', maxHeight: '300px' }}>
                    {selectedLog.new_values ? JSON.stringify(selectedLog.new_values, null, 2) : 'None'}
                  </pre>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)} variant="outlined">Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLogsPage;
